/**
 * Billing across every restaurant: the plan changes waiting for an answer,
 * and the invoices — the verbs `scripts/billing_admin.py` has, on a screen.
 *
 * No payment provider is integrated. Approving moves the plan from today and
 * (unless told not to) invoices the period; an invoice is marked paid when the
 * money arrives; a restaurant that does not pay is suspended from its own page.
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  displayLocale,
  formatMoney,
  usePlanRequests,
  usePlatformInvoices,
  usePlatformRestaurants,
  type InvoiceFilter,
  type PlatformInvoiceRow,
} from '@restaurant/api-client';
import { Button, Card, Empty, ErrorNotice, Field, Loading, Segmented } from '@restaurant/ui';

import {
  ApproveSheet,
  DeclineSheet,
  InvoiceState,
  IssueInvoiceSheet,
  PaidSheet,
  VoidSheet,
  type RequestFacts,
} from '../billing-sheets';
import { cycleWords, formatDay, formatMoment } from '../words';

const FILTERS: { value: InvoiceFilter; label: string }[] = [
  { value: 'open', label: 'Unpaid' },
  { value: 'overdue', label: 'Overdue' },
  { value: 'paid', label: 'Paid' },
  { value: 'void', label: 'Cancelled' },
  { value: 'all', label: 'All' },
];

type Open =
  | { name: 'approve'; request: RequestFacts }
  | { name: 'decline'; request: RequestFacts }
  | { name: 'issue' }
  | { name: 'paid'; invoice: PlatformInvoiceRow }
  | { name: 'void'; invoice: PlatformInvoiceRow }
  | null;

export function BillingPage() {
  const requests = usePlanRequests();
  const restaurants = usePlatformRestaurants('');
  const [show, setShow] = useState<InvoiceFilter>('open');
  const [restaurantId, setRestaurantId] = useState('');
  const invoices = usePlatformInvoices(show, restaurantId || null);
  const [open, setOpen] = useState<Open>(null);
  const locale = displayLocale();
  const close = () => setOpen(null);
  // The platform bills in one currency; any row carries it. Before there is
  // any row the label simply does not name one, rather than guess.
  const currency = requests.data?.[0]?.currency ?? invoices.data?.[0]?.currency ?? '';

  return (
    <>
      <header className="pf__head">
        <div>
          <h1 className="pf__title">Billing</h1>
          <p className="pf__sub">
            Plan changes restaurants have asked for, and every invoice the platform has issued.
          </p>
        </div>
        <Button variant="primary" onClick={() => setOpen({ name: 'issue' })}>
          Issue an invoice
        </Button>
      </header>

      <Card title="Plan changes waiting">
        {requests.isPending && <Loading label="Loading" />}
        {requests.isError && <ErrorNotice message={requests.error.message} />}
        {requests.data && requests.data.length === 0 && (
          <Empty title="Nothing waiting" description="New requests appear here." />
        )}
        {requests.data && requests.data.length > 0 && (
          <div className="pf__table-wrap">
            <table className="pf__table">
              <thead>
                <tr>
                  <th>Restaurant</th>
                  <th>Wants</th>
                  <th>Asked</th>
                  <th className="pf__num">List price</th>
                  <th>Their note</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {requests.data.map((row) => {
                  const request: RequestFacts = {
                    id: row.id,
                    restaurantId: row.restaurant_id,
                    restaurantName: row.restaurant_name,
                    planName: row.plan_name,
                    cycle: row.cycle,
                    note: row.note,
                    listPrice: row.list_price,
                    currency: row.currency,
                  };
                  return (
                    <tr key={row.id}>
                      <td>
                        <Link className="pf__rowlink" to={`/restaurants/${row.restaurant_id}`}>
                          {row.restaurant_name}
                        </Link>
                      </td>
                      <td>
                        {row.plan_name}, {cycleWords(row.cycle)}
                      </td>
                      <td>{formatMoment(row.requested_at, locale)}</td>
                      <td className="pf__num">
                        {row.list_price === null
                          ? 'Agreed'
                          : formatMoney(row.list_price, row.currency, locale)}
                      </td>
                      <td>{row.note || '—'}</td>
                      <td className="pf__actions">
                        <Button
                          size="sm"
                          variant="primary"
                          onClick={() => setOpen({ name: 'approve', request })}
                        >
                          Approve
                        </Button>
                        <Button size="sm" onClick={() => setOpen({ name: 'decline', request })}>
                          Decline
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Card title="Invoices">
        <div className="pf__toolbar">
          <Segmented label="Which invoices" options={FILTERS} value={show} onChange={setShow} />
          <Field label="Restaurant">
            <select
              className="ui-input"
              value={restaurantId}
              onChange={(e) => setRestaurantId(e.target.value)}
            >
              <option value="">Every restaurant</option>
              {restaurants.data?.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.name}
                </option>
              ))}
            </select>
          </Field>
        </div>
        {invoices.isPending && <Loading label="Loading invoices" />}
        {invoices.isError && <ErrorNotice message={invoices.error.message} />}
        {invoices.data && invoices.data.length === 0 && <Empty title="No invoices here" />}
        {invoices.data && invoices.data.length > 0 && (
          <div className="pf__table-wrap">
            <table className="pf__table">
              <thead>
                <tr>
                  <th>Number</th>
                  <th>Restaurant</th>
                  <th>For</th>
                  <th className="pf__num">Amount</th>
                  <th>Due</th>
                  <th>State</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {invoices.data.map((invoice) => (
                  <tr key={invoice.id}>
                    <td className="pf__mono">{invoice.number}</td>
                    <td>
                      <Link
                        className="pf__rowlink"
                        to={`/restaurants/${invoice.restaurant_id}`}
                      >
                        {invoice.restaurant_name}
                      </Link>
                    </td>
                    <td>{invoice.description}</td>
                    <td className="pf__num">
                      {formatMoney(invoice.amount, invoice.currency, locale)}
                    </td>
                    <td>{formatDay(invoice.due_on, locale)}</td>
                    <td>
                      <InvoiceState invoice={invoice} overdue={invoice.overdue} />
                      {invoice.payment_reference && (
                        <span className="pf__faint"> {invoice.payment_reference}</span>
                      )}
                    </td>
                    <td className="pf__actions">
                      {invoice.status === 'open' && (
                        <>
                          <Button size="sm" onClick={() => setOpen({ name: 'paid', invoice })}>
                            Mark paid
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => setOpen({ name: 'void', invoice })}
                          >
                            Cancel
                          </Button>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {open?.name === 'approve' && <ApproveSheet request={open.request} onDismiss={close} />}
      {open?.name === 'decline' && <DeclineSheet request={open.request} onDismiss={close} />}
      {open?.name === 'issue' && (
        <IssueInvoiceSheet
          restaurants={(restaurants.data ?? []).map((r) => ({ id: r.id, name: r.name }))}
          currency={currency}
          onDismiss={close}
        />
      )}
      {open?.name === 'paid' && (
        <PaidSheet
          restaurantId={open.invoice.restaurant_id}
          invoice={open.invoice}
          onDismiss={close}
        />
      )}
      {open?.name === 'void' && (
        <VoidSheet
          restaurantId={open.invoice.restaurant_id}
          invoice={open.invoice}
          onDismiss={close}
        />
      )}
    </>
  );
}
